import {
    Box,
    Button,
    Chip,
    Paper,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Tooltip,
} from '@mui/material';

import React, { useEffect, useMemo, useState } from 'react';

import { Close, Download, OpenInNew } from '@mui/icons-material';

const statuses = {
    PENDING: { label: 'Pendente', color: 'warning' },
    RECEIVED: { label: 'Recebida', color: 'success' },
    CONFIRMED: { label: 'Confirmada', color: 'success' },
    OVERDUE: { label: 'Atrasada', color: 'error' },
    REFUNDED: { label: 'Estornada', color: 'default' },
    DELETED: { label: 'Removida', color: 'default' },
    CANCELED: { label: 'Cancelada', color: 'default' },
    EXPIRED: { label: 'Expirada', color: 'default' },
    PENDING_REFUND: { label: 'Pendente de Estorno', color: 'default' },
    REFUND_FAILED: { label: 'Estorno Falhou', color: 'default' },
    REFUND_IN_PROGRESS: { label: 'Estorno em Progresso', color: 'default' },
    REFUND_PENDING: { label: 'Estorno Pendente', color: 'default' },
    RECEIVED_IN_CASH: { label: 'Recebida em dinheiro', color: 'success' },
    CHARGEBACK_REQUESTED: { label: 'Contestação', color: 'error' },
    CHARGEBACK_DISPUTE: { label: 'Contestação em disputa', color: 'error' },
    AWAITING_CHARGEBACK_REVERSAL: { label: 'Aguardando reversão', color: 'warning' },
};

function invoiceDescription(invoice) {
    const description = invoice?.description || '';
    const number = Number(invoice?.installmentNumber);
    if (!number || /parcela/i.test(description)) return description || '--';
    const label = `Parcela ${number}`;
    return description ? `${label} · ${description}` : label;
}

function normalizeInvoiceList(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [];
}

/** ID local na UI; Asaas / provider fica só no tooltip. */
function resolveInvoiceIds(invoice) {
    const externalId =
        invoice?.externalId ||
        invoice?.chargeId ||
        (typeof invoice?.id === 'string' ? invoice.id : null) ||
        null;

    const localId =
        invoice?.localId ??
        invoice?.invoiceId ??
        (typeof invoice?.id === 'number' ? invoice.id : null) ??
        null;

    return { localId, externalId };
}

function resolveInvoiceActions(invoice) {
    const status = String(invoice?.status || '').toUpperCase();
    const isPaid = ['CONFIRMED', 'RECEIVED'].includes(status);
    const isOpen = ['PENDING', 'OVERDUE'].includes(status);

    const invoiceUrl = invoice?.invoiceUrl || null;
    const bankSlipUrl = invoice?.bankSlipUrl || null;
    const receiptUrl = invoice?.transactionReceiptUrl || invoice?.receiptPayment || null;

    const actions = [];

    if (isOpen && invoiceUrl) {
        actions.push({ key: 'view', label: 'Ver fatura', url: invoiceUrl });
    } else if (isOpen && bankSlipUrl) {
        actions.push({ key: 'download-slip', label: 'Baixar fatura', url: bankSlipUrl });
    } else if (invoiceUrl) {
        actions.push({ key: 'view', label: 'Ver fatura', url: invoiceUrl });
    } else if (bankSlipUrl && !isPaid) {
        actions.push({ key: 'download-slip', label: 'Baixar fatura', url: bankSlipUrl });
    }

    if (isPaid && receiptUrl) {
        actions.push({ key: 'receipt', label: 'Baixar comprovante', url: receiptUrl });
    } else if (!actions.length && receiptUrl) {
        actions.push({ key: 'receipt', label: 'Baixar comprovante', url: receiptUrl });
    }

    return actions;
}

export function InvoicesList({
    club,
    data,
    isLoading,
    checkbox = false,
    onSelectedPaymentIdsChange,
    onDeleteCharge,
    deletingPaymentId,
}) {
    const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);

    const handleOpenInvoice = url => {
        if (!url) return;
        window.open(url, '_blank', 'noopener');
    };

    const sortedInvoices = useMemo(() => {
        const list = normalizeInvoiceList(data);
        return [...list].sort((a, b) => {
            const dateA = new Date(a.dueDate || 0);
            const dateB = new Date(b.dueDate || 0);
            return dateA - dateB;
        });
    }, [data]);

    useEffect(() => {
        if (!checkbox) {
            setSelectedPaymentIds([]);
            return;
        }

        setSelectedPaymentIds([]);
    }, [checkbox, data]);

    useEffect(() => {
        onSelectedPaymentIdsChange?.(selectedPaymentIds);
    }, [selectedPaymentIds, onSelectedPaymentIdsChange]);

    if (isLoading) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                    Carregando faturas...
                </Typography>
            </Box>
        );
    }

    if (!sortedInvoices.length) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                    Não há informações de pagamento.
                </Typography>
            </Box>
        );
    }

    function formatDate(date) {
        if (!date) return '--';
        const [year, month, day] = String(date).split('T')[0].split('-');
        if (!year || !month || !day) return '--';
        return `${day}/${month}/${year}`;
    }

    return (
        <Stack gap={3}>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {checkbox && <TableCell align="center">#</TableCell>}
                            <TableCell>ID</TableCell>
                            <TableCell>Valor</TableCell>
                            <TableCell>Vencimento</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Pagamento</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {sortedInvoices.map((invoice, index) => {
                            const { localId, externalId } = resolveInvoiceIds(invoice);
                            const rowKey = String(localId ?? externalId ?? index);
                            const selectionId = String(externalId || localId || '');
                            const invoiceActions = resolveInvoiceActions(invoice);

                            return (
                                <TableRow key={rowKey} hover>
                                    {checkbox && (
                                        <TableCell>
                                            <Checkbox
                                                disabled={['CONFIRMED', 'RECEIVED'].includes(invoice?.status)}
                                                checked={
                                                    selectionId
                                                        ? selectedPaymentIds.includes(selectionId)
                                                        : false
                                                }
                                                onChange={() => {
                                                    if (!selectionId || selectionId === 'undefined' || selectionId === 'null') {
                                                        return;
                                                    }

                                                    setSelectedPaymentIds(prev => {
                                                        if (prev.includes(selectionId)) {
                                                            return prev.filter(id => id !== selectionId);
                                                        }
                                                        return [...prev, selectionId];
                                                    });
                                                }}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Tooltip
                                            title={
                                                externalId
                                                    ? `ID externo: ${externalId}`
                                                    : 'Sem ID externo'
                                            }
                                            placement="top"
                                            enterDelay={300}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight="medium"
                                                component="span"
                                                sx={{ cursor: externalId ? 'help' : 'default' }}
                                            >
                                                {localId != null ? `#${localId}` : '—'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            R${' '}
                                            {parseFloat(invoice?.value || 0)
                                                .toFixed(2)
                                                .replace('.', ',')}
                                        </Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2">
                                            {invoice?.dueDate ? formatDate(invoice?.dueDate) : '--'}
                                        </Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Chip
                                            label={
                                                statuses[invoice?.status]?.label ||
                                                invoice?.status ||
                                                'Processando'
                                            }
                                            color={statuses[invoice?.status]?.color || 'default'}
                                            size="small"
                                        />
                                    </TableCell>

                                    <TableCell>
                                        {invoice?.paymentDate ? (
                                            <Typography variant="body2">
                                                {formatDate(invoice?.paymentDate || invoice?.confirmedDate)}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {formatDate(invoice?.confirmedDate)}
                                            </Typography>
                                        )}
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {invoiceDescription(invoice)}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="right">
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            justifyContent="flex-end"
                                            alignItems="center"
                                            flexWrap="wrap"
                                            useFlexGap
                                        >
                                            {onDeleteCharge &&
                                                !['CONFIRMED', 'RECEIVED'].includes(invoice?.status) &&
                                                selectionId && (
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        disabled={deletingPaymentId === selectionId}
                                                        onClick={() => onDeleteCharge(selectionId)}
                                                        startIcon={<Close fontSize="small" />}
                                                    >
                                                        Excluir
                                                    </Button>
                                                )}
                                            {invoiceActions.map(action => (
                                                <Button
                                                    key={action.key}
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleOpenInvoice(action.url)}
                                                    startIcon={
                                                        action.key === 'view' ? (
                                                            <OpenInNew fontSize="small" />
                                                        ) : (
                                                            <Download fontSize="small" />
                                                        )
                                                    }
                                                >
                                                    {action.label}
                                                </Button>
                                            ))}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
}
